/**
 * Menu Icon Map — resolves string icon names to MUI icon components.
 * Used by the menu system to keep the config file free of JSX imports.
 */

import { SvgIconComponent } from '@mui/icons-material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BadgeIcon from '@mui/icons-material/Badge';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DeskIcon from '@mui/icons-material/Desk';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ScienceIcon from '@mui/icons-material/Science';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccessibleIcon from '@mui/icons-material/Accessible';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import FactoryIcon from '@mui/icons-material/Factory';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import SpeedIcon from '@mui/icons-material/Speed';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import GroupsIcon from '@mui/icons-material/Groups';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EventIcon from '@mui/icons-material/Event';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DrawIcon from '@mui/icons-material/Draw';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const ICON_MAP: Record<string, SvgIconComponent> = {
  PersonAdd: PersonAddIcon,
  Business: BusinessIcon,
  AccountTree: AccountTreeIcon,
  Apartment: ApartmentIcon,
  Work: WorkIcon,
  People: PeopleIcon,
  MedicalServices: MedicalServicesIcon,
  Badge: BadgeIcon,
  Description: DescriptionIcon,
  LocalHospital: LocalHospitalIcon,
  Desk: DeskIcon,
  CalendarMonth: CalendarMonthIcon,
  Schedule: ScheduleIcon,
  DateRange: DateRangeIcon,
  HealthAndSafety: HealthAndSafetyIcon,
  Science: ScienceIcon,
  Assignment: AssignmentIcon,
  InsertChart: InsertChartIcon,
  SwapVert: SwapVertIcon,
  PlaylistAddCheck: PlaylistAddCheckIcon,
  PriceChange: PriceChangeIcon,
  LocalPharmacy: LocalPharmacyIcon,
  Search: SearchIcon,
  NotificationsActive: NotificationsActiveIcon,
  Accessible: AccessibleIcon,
  Lock: LockIcon,
  Security: SecurityIcon,
  Warning: WarningIcon,
  Factory: FactoryIcon,
  GroupWork: GroupWorkIcon,
  AssignmentLate: AssignmentLateIcon,
  Speed: SpeedIcon,
  Thermostat: ThermostatIcon,
  School: SchoolIcon,
  Groups: GroupsIcon,
  CardMembership: CardMembershipIcon,
  SyncAlt: SyncAltIcon,
  ListAlt: ListAltIcon,
  ReportProblem: ReportProblemIcon,
  Draw: DrawIcon,
  AssignmentTurnedIn: AssignmentTurnedInIcon,
  SmartToy: SmartToyIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  SupervisedUserCircle: SupervisedUserCircleIcon,
  VpnKey: VpnKeyIcon,
  Event: EventIcon,
  WhatsApp: WhatsAppIcon,
  Psychology: PsychologyIcon,
  AttachMoney: AttachMoneyIcon,
  Dashboard: DashboardIcon,
  ContactMail: ContactMailIcon,
  Receipt: ReceiptIcon,
  RequestQuote: RequestQuoteIcon,
  MenuBook: MenuBookIcon,
  Settings: SettingsIcon,
  Email: EmailIcon,
  SupportAgent: SupportAgentIcon,
};

/** Resolve icon name to MUI component. Falls back to HelpOutlineIcon. */
export function getMenuIcon(name: string): SvgIconComponent {
  return ICON_MAP[name] ?? HelpOutlineIcon;
}
